<template>
  <div>
    <InputButton
      :icon="buttonIcon"
      :fill="buttonFill"
      :label="buttonLabel"
      @click="toggleVisible"
    />
    <div v-if="visible" :class="['dropdown-picker card p-0', { 'shorter-height': needsShorterHeight }]">
      <div class="picker-search card-header">
        <input
          ref="searchInput"
          v-model="search"
          v-focus
          aria-label="Search"
          name="search"
          type="text"
          autocomplete="off"
          placeholder="Search..."
          @keydown.enter.prevent="insertSelected()"
        >
        <InputButton
          icon="times-circle"
          fill="far"
          label="Clear Search"
          :disabled="search.length === 0"
          @click="clearSearch"
        />
      </div>
      <div class="card-body d-flex flex-column h-100 py-0">
        <slot name="default" :items="filteredItems" :select="select" :insertSelected="insertSelected">
          <TabPreview v-if="hasItems" :tabs="Object.values(filteredItems).flatMap(category => Object.entries(category).map(([key, value]) => ({ [key]: value })))" :quick-submit="(value) => insertSelected(value)" @change="select">
            <template #default="{activeText, postSubmit}">
              <div class="pt-3">
                "{{ activeText }}"
              </div>
              <br>
              <button class="btn btn-primary" @click="insertSelected(activeText) && postSubmit()">
                {{ buttonLabel }}
              </button>
            </template>
          </TabPreview>
          <div v-else class="text-center mt-3">
            No matching items.
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import InputButton from "@/FormWidgets/WidgetInputArea/components/InputButton.vue";
import TabPreview from "@/FormWidgets/WidgetInputArea/components/TabPreview.vue";

export default {
  name: "InputPicker",
  // Vue 3:
  // emits: ['input', 'cancelTrigger'],
  components: {TabPreview, InputButton},
  directives: {
    focus: {
      inserted(el) {
        el.focus()
      },
    },
  },
  props: {
    buttonIcon: {
      type: String,
      required: true,
    },
    buttonFill: {
      type: String,
      required: false,
    },
    buttonLabel: {
      type: String,
      required: true,
    },
    textarea: {
      type: Function,
      required: true,
    },
    /** The last KeyboardEvent from the input. Required to enable keyboard shortcuts */
    trigger: {
      type: [KeyboardEvent, Boolean],
      default: false,
    },
    /** The start of the trigger. Required to enable keyboard shortcuts */
    triggerStart: {
      type: String, // Regex
      default: "\\.",
    },
    /** The end of the trigger. By default, it is any alphanumeric */
    triggerLastCharacter: {
      type: String, // Regex
      default: "[A-Za-z0-9]",
    },
    /** True matches only at start of keys. False matches any word start in key or value */
    anchorSearchToKeyStart: {
      type: Boolean,
      default: false,
    },
    /** Object with keys to search by and values to insert  */
    items: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      visible: false,
      selectedItem: null,
      filteredItems: this.items,
      needsShorterHeight: false,
    }
  },
  computed: {
    hasItems() {
      return Object.keys(this.filteredItems).length > 0;
    },
  },
  watch: {
    search: function(value) {
      this.filterItems(value);
      if(Object.keys(this.filteredItems).length === 0) {
        this.select(null);
      }
    },
    visible: function(value) {
      if (value) {
        this.$nextTick(() => {
          // todo: convert rem to px instead of assuming 1rem is 16px
          this.needsShorterHeight = this.$el.getBoundingClientRect().top < 320;
        });
      }
    },
    trigger: function (event) {
      // Return if there is no event (e.g., trigger was canceled)
      if(event === false){
        return;
      }
      if (event.key.length === 1 && event.key.match(new RegExp(this.triggerLastCharacter)) && this.triggerOffset() > 0) {
        this.visible = true;
        this.search = event.key;
      } else{
        this.$emit('cancelTrigger', false);
      }
    },
  },
  mounted() {
    document.addEventListener('keyup', this.escape);
    window.addEventListener('click', this.close);
  },
  destroyed() {
    document.removeEventListener('keyup', this.escape);
    window.removeEventListener('click', this.close);
  },
  methods: {
    clearSearch(){
      this.search = '';
      this.$refs.searchInput.focus();
    },
    select(item) {
      this.selectedItem = item;
    },
    toggleVisible() {
      this.visible = !this.visible;
      this.search = '';
      this.$emit('cancelTrigger', false);
    },
    hide(refocus) {
      this.visible = false;
      this.$emit('cancelTrigger', refocus);
    },
    escape(e) {
      if (this.visible === true && e.keyCode === 27) {
        this.hide(true);
      }
    },
    close(e) {
      if (! this.$el.contains(e.target)) {
        this.hide(false);
      }
    },
    preprocess(text) {
      return text.toLowerCase().replace(/[^\p{L}0-9]+/gu, ' ');
    },
    matchWordsInside(needle, haystack) {
      return this.preprocess(needle).match('\\b' + this.preprocess(haystack));
    },
    filterItems() {
      if (this.search.length === 0) {
        this.filteredItems = this.items;
        return;
      }
      const filtered = {};
      for (const category in this.items) {
        const matchedItems = {};
        for (const itemName in this.items[category]) {
          if(this.anchorSearchToKeyStart && this.preprocess(itemName).startsWith(this.preprocess(this.search))) {
          // ^ Match only at start of key
            matchedItems[itemName] = this.items[category][itemName];
          } else if (!this.anchorSearchToKeyStart &&
              (this.matchWordsInside(itemName, this.search) || this.matchWordsInside(this.items[category][itemName], this.search))
          ) {
            // ^ Match liberally
            matchedItems[itemName] = this.items[category][itemName];
          }
        }
        if (Object.keys(matchedItems).length > 0) {
          filtered[category] = matchedItems;
        }
      }
      this.filteredItems = filtered;

      // If there's exactly one match across all categories, auto-select it
      const allMatches = Object.values(filtered).flatMap(category => Object.values(category));
      if (allMatches.length === 1) {
        this.select(allMatches[0]);
      }
    },
    triggerOffset() {
      const startPos = this.textarea().selectionStart;
      const found = this.textarea().value.substring(0, startPos).match(new RegExp(this.triggerStart + this.triggerLastCharacter + '$'));
      return (this.trigger && found) ? found[0].length : 0;
    },
    insertSelected() {
      const selected = this.selectedItem;
      if(!selected) {
        return;
      }
      const startPos = this.textarea().selectionStart;
      const offset = this.triggerOffset();
      const endPos = this.textarea().selectionEnd;
      const newValue = this.textarea().value.substring(0, startPos - offset)
          + selected
          + this.textarea().value.substring(endPos, this.textarea().value.length);
      this.$emit('input', newValue);

      this.hide(true);

      this.$nextTick(() => {
        this.textarea().selectionStart = this.textarea().selectionEnd = startPos + selected.length - offset
      });
    }
  }
}
</script>

<style scoped>
/* See https://codepen.io/DCzajkowski/pen/jzLzWp */
.dropdown-picker {
  position: absolute;
  z-index: 1;
  border: 1px solid #ccc;
  /* 1000px is roughly the size of the input,
     75vm looks best at most widths
     25rem is needed for mobile */
  width: min(1000px, max(70vw, 25rem));
  max-width: 90vw;
  right: 0;
  bottom: calc(100% + 1rem);
  padding: 1rem;
  box-sizing: border-box;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 1px 1px 8px #c7dbe6;
  height: 20rem;
}

/* Applied conditionally when viewport is small to override max-height */
.dropdown-picker.shorter-height {
  height: 15rem;
}

.card-body {
  overflow: scroll;
}

.picker-search {
  display: flex;
  /* Context for position: absolute on .picker-search button */
  position: relative;
}

.picker-search > input {
  flex: 1;
  border-radius: 10rem;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  outline: none;
}

.picker-search button {
  position: absolute;
  /* Align with the right edge and vertically center with the input: */
  right: 0;
  top: 50%;
  /* Account for the size of the button, and the rounded right edge of the input: */
  transform: translate(-100%, -50%);
}

/* If it's hidden with Vue instead, it's treated as outside the dropdown in the onClick listener and closes the dropdown */
.picker-search button:disabled {
  visibility: hidden;
}

</style>
